import { fetchFromApi } from "./fetchFromApi";

const TYPE_TO_API = { roundRobin: "round_robin", swiss: "swiss", match: "match" };
const TYPE_FROM_API = { round_robin: "roundRobin", swiss: "swiss", match: "match" };

async function post(path, body = {}) {
  const response = await fetchFromApi(path, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  const result = await response.json();
  if (!response.ok || !result?.success) {
    throw new Error(result?.message || "Chess persistence request failed.");
  }
  return result;
}

export const listStoredChessTournaments = () => post("chess-list-tournaments");
export const loadStoredChessTournament = (id) => post("chess-load-tournament", { id });
export const searchStoredChessPlayers = (query) => post("chess-search-players", { query, limit: 10 });
export const saveStoredChessTournament = (payload) => post("chess-save-tournament", payload);
export const previewChessOlympiadImport = (section) => post("chess-preview-olympiad-import", { section });
export const applyChessOlympiadImport = (preview, records = preview.records, previewHash = preview.preview_hash) => post("chess-apply-olympiad-import", {
  section: preview.tournament.section,
  records,
  preview_hash: previewHash,
});
export const listChessOlympiadTeams = (section, query = "") => post("chess-list-olympiad-teams", { section, query });
export const loadChessOlympiadTeam = (section, id) => post("chess-load-olympiad-team", { section, id });
export const loadChessOlympiadField = (section) => post("chess-load-olympiad-field", { section });
export const listChessOlympiadMatches = (section, captureForecasts = true) => post("chess-list-olympiad-matches", { section, capture_forecasts: captureForecasts });
export const saveChessOlympiadMatch = (section, match) => post("chess-save-olympiad-match", { section, match });

export function serializeChessTournament({
  tournamentId,
  tournamentName,
  tournamentDate,
  tournamentType,
  baseMinutes,
  incrementSeconds,
  roundCount,
  matchGames,
  tournamentStarted,
  parameters,
  players,
  games,
}) {
  const playerIds = new Map();
  const participants = players.map((player, index) => {
    const persistentId = player.databasePlayerId || `new-player-${player.id}`;
    playerIds.set(player.id, persistentId);
    return {
      player_id: player.databasePlayerId || null,
      player: player.databasePlayerId ? undefined : {
        name: player.name,
        elo_standard: Number(player.elo),
        own_strength: player.ownStrength === "" ? null : Number(player.ownStrength),
      },
      start_number: Number(player.startNumber) || index + 1,
      name_used: player.name,
      rating_used: Number(player.elo),
      own_strength_used: player.ownStrength === "" ? null : Number(player.ownStrength),
      individual_draw_factor: Number(player.drawFactor),
      client_player_key: persistentId,
    };
  });
  const boardsByRound = new Map();

  return {
    tournament: {
      id: tournamentId || null,
      name: tournamentName,
      tournament_type: TYPE_TO_API[tournamentType],
      tournament_date: tournamentDate || null,
      base_minutes: Number(baseMinutes),
      increment_seconds: Number(incrementSeconds),
      rounds: tournamentType === "match" ? Number(matchGames) : Number(roundCount),
      status: tournamentStarted ? "in_progress" : "draft",
      model_parameters: { ...parameters },
    },
    participants,
    games: games.map((game) => {
      const round = Number(game.round);
      const boardNumber = game.boardNumber ?? (boardsByRound.get(round) || 0) + 1;
      boardsByRound.set(round, boardNumber);
      return {
        id: game.id,
        round,
        board_number: boardNumber,
        white_player_id: playerIds.get(game.whitePlayerId),
        black_player_id: game.blackPlayerId == null ? null : playerIds.get(game.blackPlayerId),
        result: game.result || null,
        result_source: game.resultSource || null,
        played: Boolean(game.played),
        completed: Boolean(game.completed),
        bye: Boolean(game.bye),
      };
    }),
  };
}

export function hydrateChessTournament(stored) {
  const localIds = new Map();
  const players = (stored.participants || []).map((participant, index) => {
    const localId = index + 1;
    localIds.set(participant.player_id, localId);
    return {
      id: localId,
      databasePlayerId: participant.player_id,
      startNumber: participant.start_number,
      name: participant.name_used,
      elo: participant.rating_used ?? "",
      ownStrength: participant.own_strength_used ?? "",
      drawFactor: participant.individual_draw_factor,
    };
  });
  const games = (stored.games || []).map((game) => ({
    id: game.id,
    round: game.round,
    boardNumber: game.board_number,
    whitePlayerId: localIds.get(game.white_player_id),
    blackPlayerId: game.black_player_id == null ? null : localIds.get(game.black_player_id),
    result: game.result,
    resultSource: game.result_source,
    played: Boolean(game.played),
    completed: Boolean(game.completed),
    probabilities: null,
    bye: Boolean(game.bye),
  }));
  return {
    tournamentId: stored.id,
    tournamentName: stored.name,
    tournamentDate: stored.tournament_date || "",
    tournamentType: TYPE_FROM_API[stored.tournament_type] || "roundRobin",
    baseMinutes: stored.base_minutes,
    incrementSeconds: stored.increment_seconds,
    rounds: stored.rounds,
    status: stored.status,
    parameters: stored.model_parameters,
    players,
    games,
  };
}

export const previewChessOlympiadResults = (section, round, candidate) => post("chess-preview-olympiad-real-import", { section, round,
  ...(candidate ? { matches: candidate.matches, team_rankings: candidate.team_rankings || [] } : {}) });
export const applyChessOlympiadResults = (section, preview) => post("chess-apply-olympiad-real-import", { section,
  matches: preview.matches, team_rankings: preview.team_rankings || [], preview_hash: preview.preview_hash });
